<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="handleCreate">
                <i class="ri-add-line"></i>
                新增座位
              </button>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋座位編號、類型..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-group">
        <select v-model="filterStoreId" class="form-control" @change="handleFilterChange">
          <option value="">全部門市</option>
          <option v-for="store in stores" :key="store.id" :value="store.id">
            {{ store.storeName }}
          </option>
        </select>
      </div>
      <div class="filter-group">
        <select v-model="filterTableType" class="form-control" @change="handleFilterChange">
          <option value="">全部類型</option>
          <option value="2人桌">2人桌</option>
          <option value="4人桌">4人桌</option>
          <option value="6人桌">6人桌</option>
          <option value="8人桌">8人桌</option>
          <option value="包廂">包廂</option>
        </select>
      </div>
      <div class="filter-group">
        <select v-model="filterIsActive" class="form-control" @change="handleFilterChange">
          <option value="">全部狀態</option>
          <option :value="true">啟用</option>
          <option :value="false">停用</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchTables">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>門市</th>
            <th>座位編號</th>
            <th>座位類型</th>
            <th>包廂類別</th>
            <th>包廂低消</th>
            <th>容納人數</th>
            <th>人數範圍</th>
            <th>樓層</th>
            <th>區域</th>
            <th>狀態</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="tables.length === 0">
            <td colspan="11" class="empty-state">
              {{ filterStoreId ? '此門市沒有座位記錄' : '沒有找到座位記錄' }}
            </td>
          </tr>
          <tr v-for="table in tables" :key="table.id">
            <td>{{ storeNameById[table.storeId] || '-' }}</td>
            <td>{{ table.tableNumber || '-' }}</td>
            <td>{{ table.tableType || '-' }}</td>
            <td>{{ table.roomCategory || '-' }}</td>
            <td>{{ table.tableType === '包廂' ? `NT$ ${Number(table.roomMinSpend || 0).toLocaleString('zh-TW')}` : '-' }}</td>
            <td>{{ table.capacity || table.maxCapacity || 0 }}人</td>
            <td>{{ `${table.minCapacity || 1}-${table.maxCapacity || table.capacity || 0}人` }}</td>
            <td>{{ table.floor || '-' }}</td>
            <td>{{ table.area || '-' }}</td>
            <td>
              <span :class="table.isActive ? 'badge badge-success' : 'badge badge-secondary'">
                {{ table.isActive ? '啟用' : '停用' }}
              </span>
            </td>
            <td>
              <div class="action-buttons">
                <button
                  class="btn btn-sm btn-secondary"
                  @click="handleEdit(table)"
                  title="編輯"
                >
                  <i class="ri-edit-line"></i>
                </button>
                <button
                  class="btn btn-sm btn-danger"
                  @click="handleDelete(table)"
                  title="刪除"
                >
                  <i class="ri-delete-bin-line"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="total > 0"
      :page="currentPage"
      :total-pages="totalPages"
      :total="total"
      @change="goToPage"
    />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { reservationService } from '@/api/modules/reservations.js';
import { storeService } from '@/api/modules/stores.js';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/swal.js';

export default {
  name: 'ClientTables',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { pageTitle } = usePageTitle('座位管理');
    const tables = ref([]);
    const stores = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const filterStoreId = ref('');
    const filterTableType = ref('');
    const filterIsActive = ref('');
    const currentPage = ref(1);
    const pageSize = ref(20);
    const total = ref(0);

    const totalPages = computed(() => Math.ceil(total.value / pageSize.value));
    const storeNameById = computed(() => {
      const map = {};
      stores.value.forEach((store) => {
        map[store.id] = store.storeName;
      });
      return map;
    });

    const syncStoreQuery = () => {
      const nextQuery = { ...route.query };
      if (filterStoreId.value) {
        nextQuery.storeId = filterStoreId.value;
      } else {
        delete nextQuery.storeId;
      }
      router.replace({ query: nextQuery });
    };

    const buildQueryParams = (page, size) => {
      const capacityMap = {
        '2人桌': 2,
        '4人桌': 4,
        '6人桌': 6,
        '8人桌': 8
      };
      return {
        page,
        pageSize: size,
        search: searchQuery.value || undefined,
        tableType: filterTableType.value || undefined,
        capacity: capacityMap[filterTableType.value] || undefined,
        isActive: filterIsActive.value !== '' ? filterIsActive.value : undefined
      };
    };

    const attachStoreName = (items, storeId) => {
      return items.map((item) => ({
        ...item,
        storeId: item.storeId || storeId
      }));
    };

    const fetchStores = async () => {
      try {
        console.log('[Tables] Fetching stores...');
        const response = await storeService.getStores({ page: 1, pageSize: 100 });
        console.log('[Tables] Stores API response:', response);
        
        if (response.success && response.data?.items) {
          stores.value = response.data.items;
          console.log('[Tables] Stores loaded:', stores.value.length);
        } else {
          console.warn('[Tables] No stores data in response');
        }
      } catch (err) {
        console.error('[Tables] Failed to fetch stores:', err);
      }
    };

    const fetchTablesForStore = async (storeId, params) => {
      const response = await reservationService.getTables(storeId, params);
      if (response.success && response.data) {
        return {
          items: attachStoreName(response.data.items || [], storeId),
          total: response.data.pagination?.totalItems || 0
        };
      }
      return { items: [], total: 0 };
    };

    const fetchTables = async () => {
      loading.value = true;
      error.value = null;

      try {
        if (filterStoreId.value) {
          const result = await fetchTablesForStore(
            filterStoreId.value,
            buildQueryParams(currentPage.value, pageSize.value)
          );
          tables.value = result.items;
          total.value = result.total;
          return;
        }

        if (stores.value.length === 0) {
          tables.value = [];
          total.value = 0;
          return;
        }

        const results = await Promise.all(stores.value.map((store) => (
          fetchTablesForStore(store.id, buildQueryParams(1, 100))
        )));
        const merged = results.flatMap((result) => result.items);
        merged.sort((a, b) => {
          const storeCompare = (storeNameById.value[a.storeId] || '').localeCompare(
            storeNameById.value[b.storeId] || '',
            'zh-Hant'
          );
          if (storeCompare !== 0) return storeCompare;
          return (a.tableNumber || '').localeCompare(b.tableNumber || '', 'zh-Hant');
        });
        total.value = merged.length;
        const start = (currentPage.value - 1) * pageSize.value;
        tables.value = merged.slice(start, start + pageSize.value);
      } catch (err) {
        error.value = err.response?.data?.message || err.message || '查詢座位記錄失敗';
        console.error('[Tables] Failed to fetch tables:', err);
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      currentPage.value = 1;
      fetchTables();
    };

    const handleCreate = () => {
      router.push({
        path: '/client/tables/create',
        query: filterStoreId.value ? { storeId: filterStoreId.value } : {}
      });
    };

    const handleEdit = (table) => {
      const storeId = table.storeId || filterStoreId.value;
      router.push({
        path: `/client/tables/${table.id}/edit`,
        query: storeId ? { storeId } : {}
      });
    };

    const handleDelete = async (table) => {
      const confirmed = await showDeleteConfirm('確定要刪除此座位嗎？');
      if (!confirmed) return;

      try {
        await reservationService.deleteTable(table.id);
        showSuccess('座位已刪除');
        fetchTables();
      } catch (err) {
        showError(err.response?.data?.message || '刪除座位失敗');
      }
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      syncStoreQuery();
      fetchTables();
    };

    const goToPage = (page) => {
      currentPage.value = page;
      fetchTables();
    };

    onMounted(async () => {
      await fetchStores();
      const queryStoreId = typeof route.query.storeId === 'string' ? route.query.storeId : '';
      if (queryStoreId && stores.value.some((store) => store.id === queryStoreId)) {
        filterStoreId.value = queryStoreId;
      } else if (stores.value.length === 1) {
        filterStoreId.value = stores.value[0].id;
      }
      await fetchTables();
    });

    return {
      pageTitle,
      tables,
      stores,
      loading,
      error,
      searchQuery,
      filterStoreId,
      storeNameById,
      filterTableType,
      filterIsActive,
      currentPage,
      totalPages,
      total,
      handleCreate,
      handleEdit,
      handleDelete,
      handleSearch,
      handleFilterChange,
      goToPage,
      fetchTables
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.table-wrapper table {
  min-width: 1100px;
}

.table-wrapper th:first-child,
.table-wrapper td:first-child {
  white-space: nowrap;
}
</style>
